import { RequirePermission } from "@components/common/require-permission";
import {
  PlanCreationMode,
  SubjectEntityIdTypes,
  SubjectTypeProps,
} from "@custom-types/base-types";
import { Button } from "@flat-ui";
import { Add } from "@mui/icons-material";
import { useAppNavigation } from "@router/use-app-navigation";
import { getPlanTableType } from "@utils/data-grid-utils/plans-data-grid-cells";
import { getPlanPermission } from "@utils/permissions/plan-permissions";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends SubjectTypeProps {
  /** ID of the subject entity that the plans are added to */
  subjectId: SubjectEntityIdTypes;
}

/** The buttons that open the page assigning a new plan, feature or license to a subject */
export function AddPlanActions({
  subjectType,
  subjectId,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { navigateToPlanCreation } = useAppNavigation();
  const addPlanPermission = getPlanPermission(subjectType, "add");

  /** Tracks the intent and opens the page that assigns the requested kind of product */
  function requestAddingPlan(
    creationMode?: Exclude<PlanCreationMode, "hardware-license">
  ): void {
    trackEvent({
      name: PlanEvents.requestAddingPlan,
      props: {
        location: getPlanTableType(subjectType),
        creationMode: creationMode ?? "hardware-license",
      },
    });
    navigateToPlanCreation({ subjectType, subjectId, creationMode });
  }

  if (subjectType === "DEVICE") {
    return (
      <RequirePermission permission={addPlanPermission}>
        <Button icon={<Add />} onClick={() => requestAddingPlan()}>
          Add License
        </Button>
      </RequirePermission>
    );
  }

  return (
    <>
      <RequirePermission permission={addPlanPermission}>
        <Button
          icon={<Add />}
          variant="secondary"
          onClick={() => requestAddingPlan("feature")}
        >
          Add Feature
        </Button>
      </RequirePermission>
      <RequirePermission permission={addPlanPermission}>
        <Button
          icon={<Add />}
          onClick={() => requestAddingPlan("subscription")}
        >
          Add Subscription
        </Button>
      </RequirePermission>
    </>
  );
}
